import { Controller } from "@hotwired/stimulus";
import Swiper from "swiper";

/**
 * Employee Slider Controller
 *
 * Handles the Swiper slider for the employee-slider block.
 * Displays circular employee images with the active one larger in the center.
 * Updates the content area (quote, name, role) when slides change.
 */
export default class extends Controller {
  static targets = ["container", "contentArea"];

  connect() {
    this.initSwiper();
  }

  disconnect() {
    if (this.swiper && this.containerTarget?.swiper === this.swiper) {
      this.swiper.destroy(true, true);
      delete this.containerTarget.swiper;
      this.swiper = null;
    }
  }

  /**
   * Initialize Swiper instance
   */
  initSwiper() {
    if (!this.hasContainerTarget) return;

    // Prevent duplicate Swiper instances if the controller is registered more than once
    if (this.containerTarget.swiper) {
      this.swiper = this.containerTarget.swiper;
      return;
    }

    const slides = this.containerTarget.querySelectorAll(".swiper-slide");
    const slideCount = slides.length;

    const swiperConfig = {
      slidesPerView: "auto",
      centeredSlides: true,
      slideToClickedSlide: true,
      grabCursor: true,
      loop: true,
      loopedSlides: slideCount,
      loopAdditionalSlides: 4,
      // freeMode: true,  // Incompatible with loop mode
      on: {
        slideChange: () => {
          this.updateContentArea();
        },
      },
    };

    this.swiper = new Swiper(this.containerTarget, swiperConfig);

    // Keep a reference on the DOM element so we don't re-initialize
    this.containerTarget.swiper = this.swiper;
  }

  /**
   * Update the content area with the active employee's data
   */
  updateContentArea() {
    if (!this.swiper || !this.hasContentAreaTarget) return;

    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    if (!activeSlide) return;

    const employeeDataJson = activeSlide.dataset.employee;
    if (!employeeDataJson) return;

    try {
      const employeeData = JSON.parse(employeeDataJson);

      // Build the HTML for the content area
      const html = `
        <blockquote class="text-lg md:text-xl italic text-nr-text max-w-3xl mx-auto mb-0">
          "${this.escapeHtml(employeeData.quote)}"
        </blockquote>
        <p class="font-bold text-nr-text mt-4 mb-0 pb-0!">
          ${this.escapeHtml(employeeData.name)}
        </p>
        <p class="text-nr-text/70 mt-1 mb-0">
          ${this.escapeHtml(employeeData.role)}
        </p>
      `;

      this.contentAreaTarget.innerHTML = html;
    } catch (error) {
      console.error("Error parsing employee data:", error);
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text || "";
    return div.innerHTML;
  }
}
